import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { FormSheet, OfficialUseBlock, SectionBand } from '../components/FormSheet'
import { getComplaint, officialDate, officialTimestamp, type Complaint } from '../lib/complaints'

export function ReceiptPage() {
  const { caseNumber } = useParams<{ caseNumber: string }>()
  const [complaint, setComplaint] = useState<Complaint | null>(null)
  const [notice, setNotice] = useState('')

  useEffect(() => {
    let current = true
    getComplaint(caseNumber)
      .then((found) => current && setComplaint(found))
      .catch((error) => current && setNotice(error instanceof Error ? error.message : 'Lookup failed.'))
    return () => {
      current = false
    }
  }, [caseNumber])

  if (notice) {
    return (
      <FormSheet>
        <SectionBand number={4} title="Docket lookup" />
        <div className="px-4 py-8 sm:px-7">
          <p className="font-form text-base font-semibold text-stamp">{notice}</p>
          <p className="mt-2 font-form text-sm text-carbon/70">
            Check the number against your receipt, or file a new complaint.
          </p>
          <FileAnother />
        </div>
      </FormSheet>
    )
  }

  if (!complaint) {
    return (
      <FormSheet>
        <div className="px-4 py-16 text-center font-form text-[11px] uppercase tracking-[0.22em] text-carbon/50 sm:px-7">
          Retrieving docket…
        </div>
      </FormSheet>
    )
  }

  return (
    <FormSheet stamp={<ReceivedStamp filedAt={complaint.createdAt} />}>
      {/* A receipt for a matter the department has answered does not go on
          promising to answer it. */}
      <div className="px-4 pb-6 pt-8 text-center sm:px-7">
        <h2 className="font-display text-[1.35rem] font-semibold uppercase leading-tight tracking-[0.06em] text-carbon sm:text-[1.7rem]">
          {complaint.ruling
            ? 'Your complaint has been ruled upon.'
            : 'Thank you for filing your complaint.'}
        </h2>
        <p className="mx-auto mt-3 max-w-[30rem] font-form text-sm leading-relaxed text-carbon/75">
          {complaint.ruling
            ? 'The department has reviewed your grievance. Its ruling is set out below.'
            : 'Your grievance has been entered into the docket. It will be reviewed in the order it was received.'}
        </p>
      </div>

      <OfficialUseBlock>
        <p className="font-form text-[10px] uppercase tracking-[0.2em] text-process/70">Case number</p>
        <p className="mt-1.5 break-words font-type text-[15px] font-bold leading-snug text-carbon sm:text-lg">
          {complaint.caseNumber}
        </p>
        <CopyCaseNumber caseNumber={complaint.caseNumber} />
        <p className="mt-3 border-t border-process/25 pt-3 font-form text-[12px] leading-relaxed text-carbon/75">
          Retain this number. Include it in all future correspondence regarding this matter.
        </p>
      </OfficialUseBlock>

      <SectionBand number={4} title="Record of filing" />
      <dl className="grid grid-cols-1 gap-x-8 gap-y-3 px-4 pb-4 pt-5 sm:grid-cols-2 sm:px-7">
        <Entry label="Complainant" value={complaint.filedBy} />
        <Entry label="Respondent" value={complaint.respondent} />
        <Entry label="Date received" value={officialDate(complaint.createdAt)} />
        <Entry label="Status" value={complaint.status} />
        <Entry label="Estimated review time" value={complaint.reviewEstimate} />
      </dl>

      <div className="px-4 pb-5 sm:px-7">
        <p className="font-form text-[10px] uppercase tracking-[0.18em] text-carbon/55">
          Statement of grievance as filed
        </p>
        {/* Shown back on the same printed rules it was typed onto. */}
        <p className="ruled-field mt-1.5 whitespace-pre-wrap break-words border border-carbon/15 px-2 font-type text-[15px] text-carbon">
          {complaint.complaintText}
        </p>
      </div>

      {complaint.ruling && <Ruling complaint={complaint} />}

      <div className="border-t border-carbon/20 px-4 py-5 sm:px-7">
        <p className="font-form text-[11px] leading-relaxed text-carbon/60">
          No further action is required of the complainant at this time. Do not file a duplicate
          complaint regarding the same matter.
        </p>
        <FileAnother />
      </div>
    </FormSheet>
  )
}

/**
 * The department's answer, printed on the receipt the complainant was told to
 * keep. Set on the same ruled lines the grievance was typed onto: a ruling is
 * something a person wrote on the form, not something the form said.
 */
function Ruling({ complaint }: { complaint: Complaint }) {
  return (
    <>
      <SectionBand number={5} title="Ruling of the department" />
      <div className="px-4 pb-5 pt-5 sm:px-7">
        <p className="ruled-field whitespace-pre-wrap break-words border border-carbon/15 px-2 font-type text-[15px] text-carbon">
          {complaint.ruling}
        </p>
        <div className="mt-3 flex flex-wrap justify-between gap-x-6 gap-y-1">
          {complaint.ruledAt !== null && (
            <p className="font-form text-[10px] uppercase tracking-[0.18em] text-carbon/55">
              Issued {officialTimestamp(complaint.ruledAt)}
            </p>
          )}
          <p className="font-form text-[10px] uppercase tracking-[0.18em] text-carbon/55">
            {complaint.noticeSentAt !== null
              ? `Notice sent ${officialDate(complaint.noticeSentAt)}`
              : 'Notice not sent'}
          </p>
        </div>
        <p className="mt-4 font-form text-[11px] leading-relaxed text-carbon/70">
          This matter is closed. A ruling of the department is final and is not subject to appeal,
          reconsideration, or discussion at the dinner table.
        </p>
      </div>
    </>
  )
}

function Entry({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-b border-dotted border-carbon/25 pb-1.5">
      <dt className="font-form text-[10px] uppercase tracking-[0.18em] text-carbon/55">{label}</dt>
      <dd className="mt-0.5 font-type text-[15px] text-carbon">{value}</dd>
    </div>
  )
}

function FileAnother() {
  return (
    <Link
      to="/"
      className="mt-5 block w-full border border-carbon/60 px-6 py-3 text-center font-form text-sm font-bold uppercase tracking-[0.2em] text-carbon transition-colors hover:bg-carbon hover:text-file"
    >
      File another complaint
    </Link>
  )
}

/** The long number is meant to be quoted, so it is also meant to be copied. */
function CopyCaseNumber({ caseNumber }: { caseNumber: string }) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(caseNumber)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <button
      type="button"
      onClick={copy}
      className="mt-2 font-form text-[10px] font-semibold uppercase tracking-[0.18em] text-process underline underline-offset-4 hover:text-carbon"
    >
      {copied ? 'Copied' : 'Copy case number'}
    </button>
  )
}

function ReceivedStamp({ filedAt }: { filedAt: number }) {
  return (
    <div
      aria-hidden="true"
      className="stamp-down pointer-events-none absolute right-2 top-3 select-none border-[3px] border-stamp/65 px-2.5 py-1 text-stamp/75 sm:right-5 sm:top-5 sm:px-4 sm:py-1.5"
    >
      <p className="font-form text-xs font-bold uppercase tracking-[0.22em] sm:text-base">Received</p>
      <p className="font-form text-[8px] uppercase tracking-[0.14em] sm:text-[10px]">
        {officialDate(filedAt)}
      </p>
    </div>
  )
}
